import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import {
  assetDescriptorHasUsage,
  dataContext,
  dataManager,
  isReadOnlySource,
  isTypeId,
  Project,
  QueryRequest,
  TypeId,
  VFSPath,
  type Agent,
  type AssetDescriptor,
  type ProcessAssetUsage,
} from '@sdk';
import { LaunchingAgentRow } from '@src/components/agents/LaunchingAgentRow';
import { Popover, PopoverContent, PopoverTrigger } from '@src/components/ui/popover';
import { Dialog, DialogContent, DialogTitle } from '@src/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/components/ui/dropdown-menu';
import { useEntitiesQuery } from '@src/hooks/entity-hooks';
import { useProcessAssets, type UseProcessAssetsResult } from './useProcessAssets';
import { additionalDirScope, assetScope, AssetScopeChip, type AssetScope } from './asset-scope';
import { assetBoardScope, BOARD_SCOPES, loadShownScopes, saveShownScopes, type BoardScope } from './board-scope';
import { AssetScopeToggles } from './AssetScopeToggles';
import { workerIcon, workerLabel } from '@src/components/lens-viewer/shared/transcript-features/transcript-utils';
import { FusionSpinner } from '@src/components/icons/FusionSpinner';
import { WikiTip } from '@src/components/wiki-tip';
import { labelForType } from '@src/components/graph-view/icons/iconRegistry';
import { EntityIcon, useEntityLocationLabel } from '@src/components/graph-view/ui/EntityIcon';
import {
  basename as _basename,
  descriptorKey,
  dirname,
  displayLabelForDescriptor as _displayLabelForDescriptor,
  isOpenableTypeid as _isOpenableTypeid,
  normalizePath,
  parseTypeid as _parseTypeid,
} from './asset-row-helpers';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { LOCAL_COMPUTE_NODE } from '@src/navigation/asset-doc-types';

import { cn } from '@src/lib/utils';
import {
  ArrowLeft,
  ArrowDownAZ,
  Boxes,
  ChevronRight,
  CircleHelp,
  Folder,
  FolderOpen,
  FolderPlus,
  Loader2,
  Plus,
  Search,
  Sparkles,
  SquareArrowOutUpRight,
  WandSparkles,
  X,
} from 'lucide-react';

/** The wiki page behind the improve wand's tip. */
const ASSET_IMPROVEMENT_WIKI = 'Asset improvement';

/**
 * One column template, applied identically to every row (asset rows and dir
 * rows alike) so cells line up down the list:
 *
 *   name chip │ scope (icon + name) │ explorer │ wand │ select
 *
 * Deliberately not CSS subgrid: a row carries its own background (selected rows
 * are tinted) and bottom border, which `display: contents` would throw away.
 * Fixed tracks align by construction, and cost nothing.
 *
 * The corollary is that optional cells still have to occupy their track — see
 * `GridCellSpacer`. Rows whose wand or un-select button is absent were the whole
 * reason nothing lined up before.
 *
 * The track widths live in styles/index.css (`.asset-grid-row`) because they
 * respond to the row's width: a narrow host gets the dense layout, where the
 * scope name drops out and only its icon remains. A grid can't container-query
 * itself, so the outer div is the container and the inner one is the grid.
 */
function AssetGridRow({ className, children, ...rest }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('asset-grid-row-host border-b last:border-b-0', className)} {...rest}>
      <div className="asset-grid-row grid items-center gap-2 px-3 py-1.5">{children}</div>
    </div>
  );
}

/** Holds a grid track open where an optional cell isn't rendered. */
function GridCellSpacer() {
  return <span aria-hidden />;
}

/**
 * Section divider — a centered, tinted label ruled out to both edges. Marks the
 * usage axis (used vs available), the coarsest split in the list.
 */
function AssetSectionRule({ testid, children }: { testid: string; children: React.ReactNode }) {
  return (
    <div
      className="sticky top-0 z-[1] flex items-center gap-2 border-b bg-popover/95 px-3 py-1.5 backdrop-blur"
      data-testid={testid}
    >
      <span className="h-px flex-1 bg-primary/30" aria-hidden />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">{children}</span>
      <span className="h-px flex-1 bg-primary/30" aria-hidden />
    </div>
  );
}

/** Sub-category header inside a section — the asset type ("Skills", "Agents"). */
function AssetTypeHeader({ testid, children }: { testid: string; children: React.ReactNode }) {
  return (
    <div
      className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-foreground/80"
      data-testid={testid}
    >
      {children}
    </div>
  );
}

export interface AssetManagerPopoverProps {
  /** The popover trigger; passed to PopoverTrigger asChild. Ignored when `centered`. */
  trigger?: React.ReactNode;
  /** Controlled open state. Omit to let the popover manage its own. */
  open?: boolean;
  /** Required alongside `open`. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Preferred side to open on. Defaults to `'bottom'`. Pass `'top'` when the
   * trigger sits near the bottom of the viewport (e.g. a message composer) so
   * the list opens upward. Collision detection still flips it back if there's
   * no room. Ignored when `centered`.
   */
  side?: 'top' | 'bottom';
  /**
   * Render as a centered modal dialog instead of a popover anchored to the
   * trigger. Use when the trigger is incidental (a fan-out menu item) or when
   * the surface must escape an enclosing menu.
   */
  centered?: boolean;
  /** Placeholder for the filter box. */
  searchPlaceholder?: string;

  /**
   * Host-owned asset list. Omit and the popover fetches the project-wide
   * staging list itself. Process-backed hosts pass their own
   * `useProcessAssets(process, …)` so EMBEDDED / ADDITIONAL_DIR / usage state
   * is present.
   */
  assets?: UseProcessAssetsResult;
  /**
   * The Agent this process was launched through (`useLaunchingAgent`). Pinned
   * at the top of the list as the run's principal — avatar + name, click opens
   * the agent. Absent for a process not spawned from an Agent.
   */
  agent?: Agent | null;
  /** Restrict the visible candidates. Default: everything the source returned. */
  filter?: (descriptor: AssetDescriptor) => boolean;

  // ── selection: status in, actions out ──────────────────────────────────
  /**
   * Already-chosen assets, keyed by TYPEID. Keying by typeid rather than
   * (typeid, source) is deliberate: attaching a `user_dir` asset makes a second
   * `embedded` row appear, and both must read as selected.
   */
  selectedTypeIds?: readonly string[];
  /**
   * A row was chosen. Receives the whole descriptor — hosts that only want the
   * ref read `d.typeid`, while pick surfaces need `posix_path`/`source`.
   *
   * Optional: omitting it makes the surface a READ-ONLY board — no select
   * control renders at all, and the two sections read purely as "what this run
   * used" vs "what is available". That is what the process asset manager wants;
   * pick surfaces (composer attach, favorites, automation rail) supply it.
   */
  onPick?: (descriptor: AssetDescriptor) => void | Promise<void>;
  /**
   * A selected row was un-chosen. Absent ⇒ this is a one-shot surface: no
   * un-select control, and the list closes after a pick. Supplying it is what
   * makes the surface multi-select, so the two can't fall out of step.
   */
  onUnpick?: (descriptor: AssetDescriptor) => void | Promise<void>;
  /**
   * Opening a row (its name chip) shows the asset in a NEW window rather than this one. For a host
   * that holds unsaved input -- a form's Attach -- where leaving the screen would discard it.
   */
  openInWindow?: boolean;

  // ── process-derived status (props) ─────────────────────────────────────
  /** Whether the Flowpad Assistant is mounted. Labels the "+" menu's
   *  mount/unmount item; `onToggleAssistant` is what makes it render. */
  assistantEnabled?: boolean;
  /** The run's worker (`claude_code`, `codex`, …) — names the worker scope toggle. */
  workerType?: string | null;
  /** Directories mounted into the process via `--add-dir`. */
  additionalDirs?: readonly string[];
  /** `descriptorKey()` of the asset whose improvement is currently running. */
  improveBusyKey?: string | null;
  /**
   * Whether an asset can actually be improved. Supplied by the host because it
   * owns the improve flow (and the type registry it consults) — deriving it
   * here too would let the wand appear on rows the host then refuses.
   */
  canImprove?: (descriptor: AssetDescriptor) => boolean;

  // ── actions (events) — each one also gates its own affordance ──────────
  onToggleAssistant?: () => void | Promise<void>;
  onAddFolder?: () => void | Promise<void>;
  onRemoveDir?: (path: string) => void | Promise<void>;
  onAddProjectDir?: (path: string) => void | Promise<void>;
  onImprove?: (descriptor: AssetDescriptor) => void;
}

/** Shared empty default — a stable identity, so optional array props don't
 *  invalidate the memos that depend on them on every render. */
const NONE: readonly string[] = [];

/**
 * Skills + agents only — the candidate set for "run something on this".
 * Workflows are deliberately excluded: they are runnable themselves rather than
 * embeddable into another process.
 */
export const RUNNABLE_ASSETS = (d: AssetDescriptor): boolean =>
  d.typeid.startsWith('skill-') || d.typeid.startsWith('subagent-');

/**
 * Reusable asset list popover.
 *
 * Purely presentational: it renders the descriptor list it is given and emits
 * events. Everything that reads or mutates an `AgenticProcess` lives in the
 * host (see `AssetManagerButton`), which passes the resulting status down as
 * props. An affordance renders iff the callback implementing it was supplied.
 *
 * Each row is a single ``(typeid, source)`` descriptor. The same typeid may
 * appear multiple times with different sources (e.g. a skill that's both
 * EMBEDDED and USER_DIR), and selection is keyed by typeid so every row for one
 * asset reads as selected together.
 */
export function AssetManagerPopover({
  trigger,
  open: controlledOpen,
  onOpenChange,
  side = 'bottom',
  centered = false,
  searchPlaceholder,
  assets,
  agent = null,
  filter,
  selectedTypeIds = NONE,
  onPick,
  onUnpick,
  openInWindow = false,
  assistantEnabled,
  additionalDirs = NONE,
  improveBusyKey = null,
  canImprove,
  workerType,
  onToggleAssistant,
  onAddFolder,
  onRemoveDir,
  onAddProjectDir,
  onImprove,
}: AssetManagerPopoverProps) {
  const { t } = useLingui();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onOpenChange?.(next);
      else setUncontrolledOpen(next);
    },
    [isControlled, onOpenChange],
  );
  const [view, setView] = useState<'list' | 'pick-project'>('list');
  const [listFilter, setListFilter] = useState('');
  const [sortBy, setSortBy] = useState<'scope' | 'name'>('scope');

  // No host list supplied (a pick surface with no process): fetch the
  // project-wide staging list ourselves. `enabled` is false whenever the host
  // owns the data, so only one fetch is ever in flight.
  const ownAssets = useProcessAssets(null, { enabled: open && !assets });

  const { descriptors: listDescriptors, isLoading: listIsLoading, error: listError, scanIssues, truncated } =
    assets ?? ownAssets;

  // Which scopes the list shows — one header toggle each. Per viewer, kept
  // across opens and reloads.
  const [shownScopes, setShownScopes] = useState<Set<BoardScope>>(loadShownScopes);
  const toggleScope = useCallback((scope: BoardScope) => {
    setShownScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      saveShownScopes(next);
      return next;
    });
  }, []);

  // Project picker — load once when entering pick-project mode.
  const projectsQuery = useMemo(() => new QueryRequest({ type: Project.type }), []);
  const { data: allProjects = [] } = useEntitiesQuery<Project>(projectsQuery, {
    enabled: open && view === 'pick-project',
  });
  const [projectQuery, setProjectQuery] = useState('');

  /** Leave a drill-down / picker, dropping the filter that belonged to it. */
  const backToList = useCallback(() => {
    setView('list');
    setListFilter('');
    setProjectQuery('');
  }, []);

  const handlePickProject = useCallback(
    async (path: string) => {
      if (!onAddProjectDir || !path) return;
      await onAddProjectDir(path);
      setView('list');
      setProjectQuery('');
    },
    [onAddProjectDir],
  );

  // Pre-fetch every descriptor's entity into the dataManager cache so the
  // chip text resolves to ``entity.displayName`` (typeid → real name) on
  // first render. ``getByTypeIdFromCache`` is sync and returns null for
  // entities not yet loaded; fetching them populates the cache and bumps
  // ``entityVersion`` to trigger a re-render that picks up the resolved
  // display labels. Without this, freshly-discovered entities show their
  // raw ``<type>-<uuid>`` typeid until something else loads them.
  const [entityVersion, setEntityVersion] = useState(0);
  useEffect(() => {
    if (!listDescriptors.length) return;
    const missing = listDescriptors
      .filter((d) => isTypeId(d.typeid))
      .map((d) => new TypeId(d.typeid))
      .filter((t) => !dataManager.getByTypeIdFromCache(t));
    if (!missing.length) return;
    let cancelled = false;
    void Promise.all(missing.map((t) => dataManager.getByTypeId(t).catch(() => null))).then(() => {
      if (!cancelled) setEntityVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [listDescriptors]);

  // The list has two nested axes, and they are different questions:
  //   section — is this asset already in play (selected, or used by the run)?
  //   group   — WHAT is it (skill / agent / …)?
  // The scope chip answers the third (where does it live), per row. One row per
  // (typeid, source) pair, so an asset visible through two sources stays visible
  // twice — that duplication is the point.
  //
  // ``entityVersion`` participates in the deps so newly-loaded entities trigger a
  // fresh build (display labels resolve from the cache).
  const sections_ = useMemo(() => {
    void entityVersion;

    // Resolve each descriptor's scope + label ONCE. Both are cache lookups that
    // allocate (a tooltip string, a display label), and a comparator would
    // otherwise re-derive them O(n log n) times — ~20k times for a 1000-asset
    // staging list, on every keystroke in the filter box.
    const selected = new Set(selectedTypeIds);
    // The host's `filter` scopes what it will accept a pick of.
    const candidates = filter ? listDescriptors.filter(filter) : listDescriptors;
    const allRows = candidates.map((d) => ({
      d,
      type: _parseTypeid(d.typeid).type,
      boardScope: assetBoardScope(d),
      scope: assetScope(d),
      label: _displayLabelForDescriptor(d),
      used: assetDescriptorHasUsage(d),
      selected: assets?.workerScoped ? !!d.attached : selected.has(d.typeid),
      improvable: !!canImprove?.(d),
      key: descriptorKey(d),
    }));
    type Row = (typeof allRows)[number];

    // Counted before the text filter, so a toggle's number doesn't move while typing.
    const scopeCounts = Object.fromEntries(BOARD_SCOPES.map((s) => [s, 0])) as Record<BoardScope, number>;
    for (const r of allRows) scopeCounts[r.boardScope] += 1;
    const rows = allRows.filter((r) => shownScopes.has(r.boardScope));

    const q = listFilter.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) =>
          [r.label, r.d.typeid, r.d.source, r.scope.label, r.d.posix_path, r.d.source_dir].some(
            (v) => typeof v === 'string' && v.toLowerCase().includes(q),
          ),
        )
      : rows;

    const byRow = (a: Row, b: Row) => {
      if (sortBy === 'scope' && a.scope.label !== b.scope.label) {
        return a.scope.label.localeCompare(b.scope.label);
      }
      if (a.label !== b.label) return a.label.localeCompare(b.label);
      return a.d.source.localeCompare(b.d.source);
    };

    const groupByType = (subset: Row[]) => {
      const buckets = new Map<string, Row[]>();
      for (const r of subset) {
        const bucket = buckets.get(r.type);
        if (bucket) bucket.push(r);
        else buckets.set(r.type, [r]);
      }
      return (
        [...buckets.entries()]
          // labelForType, not the useAssetTypes array: that one is view-mode
          // filtered and can miss a type Vibe mode still has descriptors for.
          .map(([type, rows]) => ({ type, label: labelForType(type), rows: rows.sort(byRow) }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    };

    const list = [
      { key: 'used' as const, label: t`Used assets`, groups: groupByType(filtered.filter((r) => r.used)) },
      { key: 'selected' as const, label: assets?.workerScoped ? t`Attached assets` : t`Selected assets`, groups: groupByType(filtered.filter((r) => r.selected && !r.used)) },
      {
        key: 'available' as const,
        label: assets?.workerScoped ? t`Available assets` : t`Project assets`,
        groups: groupByType(filtered.filter((r) => !r.selected && !r.used && (!assets?.workerScoped || r.d.available === true))),
      },
      {
        key: 'other' as const,
        label: t`Other assets`,
        groups: groupByType(filtered.filter((r) => assets?.workerScoped && !r.selected && !r.used && r.d.available !== true)),
      },
    ].filter((s) => s.groups.length > 0);
    return { list, scopeCounts };
  }, [assets?.workerScoped, canImprove, entityVersion, filter, listDescriptors, listFilter, selectedTypeIds, shownScopes, sortBy, t]);
  const { list: sections, scopeCounts } = sections_;

  // Pinned rows (the launching agent) sit above the sections and are not
  // filterable — they hide while a filter is typed.
  const showPinnedRows = !listFilter.trim();

  const filteredDirs = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    if (!q) return additionalDirs;
    return additionalDirs.filter((p) => p.toLowerCase().includes(q));
  }, [additionalDirs, listFilter]);

  // Transcript usage that matched no asset. Expected to be empty; listed last,
  // and only when there is something to list.
  const unresolvedUsage = (assets ?? ownAssets).unresolvedUsage;
  const unknownUsage = useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    return (unresolvedUsage ?? []).filter((u) => !q || u.reference.toLowerCase().includes(q));
  }, [unresolvedUsage, listFilter]);

  const filteredProjects = useMemo(() => {
    const q = projectQuery.trim().toLowerCase();
    const list = allProjects.filter((p) => {
      const path = (p as { fs_storage_mount_path?: string }).fs_storage_mount_path;
      return !!path; // hide projects without a mount path
    });
    if (!q) return list;
    return list.filter((p) => {
      const name = (p.displayName ?? '').toLowerCase();
      const path = ((p as { fs_storage_mount_path?: string }).fs_storage_mount_path ?? '').toLowerCase();
      return name.includes(q) || path.includes(q);
    });
  }, [allProjects, projectQuery]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        setView('list');
        setProjectQuery('');
        setListFilter('');
      }
    },
    [setOpen],
  );

  const handlePick = useCallback(
    async (descriptor: AssetDescriptor) => {
      if (!onPick) return;
      // A surface with no un-select is one-shot: close on pick. Settle our own
      // open state BEFORE emitting, because such a host routinely unmounts this
      // component from inside `onPick` (the automation rail swaps itself for a
      // drawer), and setting state afterwards would write to an unmounted tree.
      if (!onUnpick) handleOpenChange(false);
      await onPick(descriptor);
    },
    [handleOpenChange, onPick, onUnpick],
  );

  const body = (
    <>
      {/* Header */}
      <div className="flex items-center gap-1.5 border-b px-3 py-2">
        {view !== 'list' && (
          <button
            type="button"
            className="-ms-1 flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            onClick={backToList}
            title={t`Back`}
            data-testid="asset-manager-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        )}
        <Boxes className="h-3.5 w-3.5 text-muted-foreground" />
        {/* Breadcrumb — the root crumb is itself the way back up, so the trail
            and the back button are the same navigation, written twice on
            purpose (one is discoverable, the other is where the eye already is). */}
        <span className="flex min-w-0 items-center gap-1 text-xs font-medium" data-testid="asset-manager-breadcrumb">
          {view === 'list' ? (
            <Trans>Assets</Trans>
          ) : (
            <>
              <button
                type="button"
                onClick={backToList}
                className="rounded text-muted-foreground hover:text-foreground hover:underline"
                data-testid="asset-manager-crumb-root"
              >
                <Trans>Assets</Trans>
              </button>
              <ChevronRight className="h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0 truncate">
                <Trans>Pick project folder</Trans>
              </span>
            </>
          )}
        </span>
        {view === 'list' && (
          <div className="ms-auto flex items-center gap-1">
            <AssetScopeToggles
              counts={scopeCounts}
              shown={shownScopes}
              onToggle={toggleScope}
              workerLabel={workerType ? workerLabel(workerType) : undefined}
              workerIcon={workerType ? workerIcon(workerType) : undefined}
            />
          </div>
        )}
      </div>

      {view !== 'pick-project' ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-1.5 border-b bg-muted/20 px-2 py-1.5">
            <Search className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              placeholder={searchPlaceholder ?? t`Filter…`}
              value={listFilter}
              onChange={(e) => setListFilter(e.target.value)}
              className="min-w-0 flex-1 rounded border bg-background px-1.5 py-0.5 text-[11px] outline-none focus:ring-1 focus:ring-ring"
              data-testid="asset-manager-list-filter"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'scope' | 'name')}
              className="rounded border bg-background px-1 py-0.5 text-[11px] outline-none focus:ring-1 focus:ring-ring"
              title={t`Sort within each type`}
              data-testid="asset-manager-list-sort"
            >
              <option value="scope">
                <Trans>By scope</Trans>
              </option>
              <option value="name">
                <Trans>By name</Trans>
              </option>
            </select>
            <ArrowDownAZ className="h-3 w-3 flex-shrink-0 text-muted-foreground" aria-hidden />
            {/* Prop-gated: pick surfaces supply neither handler, so no
                  DropdownMenu mounts there at all — which is what keeps this
                  component safe to drop into a composer that forbids nested
                  popovers. */}
            {(onAddFolder || onAddProjectDir || onToggleAssistant) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    title={t`Add`}
                    className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    data-testid="asset-manager-add-menu"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {onAddFolder && (
                    <DropdownMenuItem
                      onSelect={() => {
                        void onAddFolder();
                      }}
                      data-testid="asset-manager-add-folder"
                    >
                      <FolderOpen className="me-2 h-3.5 w-3.5" />
                      <Trans>Folder…</Trans>
                    </DropdownMenuItem>
                  )}
                  {onAddProjectDir && (
                    <DropdownMenuItem
                      onSelect={() => {
                        setProjectQuery('');
                        setView('pick-project');
                      }}
                      data-testid="asset-manager-add-project-folder"
                    >
                      <FolderPlus className="me-2 h-3.5 w-3.5" />
                      <Trans>Project folder…</Trans>
                    </DropdownMenuItem>
                  )}
                  {/* The Flowpad Assistant is one more folder mounted into the
                      worker; its assets then show under the Assistant toggle. */}
                  {onToggleAssistant && (
                    <DropdownMenuItem
                      onSelect={() => {
                        void onToggleAssistant();
                      }}
                      title={t`A restart of the worker is required.`}
                      data-testid="asset-manager-assistant-mount"
                      data-enabled={assistantEnabled ? 'true' : 'false'}
                    >
                      <Sparkles className="me-2 h-3.5 w-3.5" />
                      {assistantEnabled ? <Trans>Unmount Flowpad Assistant</Trans> : <Trans>Mount Flowpad Assistant</Trans>}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="asset-manager-list">
            {/* The run's principal — the Agent it was launched through. */}
            {showPinnedRows && agent && <LaunchingAgentRow agent={agent} testId="asset-manager-launching-agent" className="m-1 w-[calc(100%-0.5rem)]" />}
            {filteredDirs.map((path) => <DirRow key={`dir|${path}`} path={path} onRemove={onRemoveDir} />)}
            {!!scanIssues?.length && (
              <details className="px-3 py-2 text-xs text-amber-700" data-testid="asset-scan-issues">
                <summary><Trans>Some assets could not be read.</Trans></summary>
                {scanIssues.map((issue, index) => <div key={`${issue.path}:${index}`} className="mt-1 break-words">{issue.path}: {issue.message}</div>)}
              </details>
            )}
            {truncated && <div className="px-3 py-2 text-xs text-muted-foreground"><Trans>Asset list truncated. Narrow the selection to see more.</Trans></div>}
            {listError && (
              <div role="alert" className="px-3 py-4 text-center text-[11px] text-destructive">
                <Trans>Could not verify available assets.</Trans>
              </div>
            )}
            {!listError && sections.length === 0 && unknownUsage.length === 0 &&
              filteredDirs.length === 0 &&
              (listIsLoading ? (
                <div
                  className="flex items-center justify-center gap-2 px-3 py-4 text-[11px] text-muted-foreground"
                  data-testid="asset-manager-loading"
                >
                  <FusionSpinner size="xs" />
                  <span>
                    <Trans>Loading assets…</Trans>
                  </span>
                </div>
              ) : (
                <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">
                  {listFilter.trim() ? (
                    <Trans>No matches.</Trans>
                  ) : BOARD_SCOPES.some((scope) => scopeCounts[scope] > 0) ? (
                    <Trans>Turn on a scope above to see its assets.</Trans>
                  ) : (
                    <Trans>No assets available.</Trans>
                  )}
                </div>
              ))}
            {sections.map((section) => (
              <Fragment key={section.key}>
                <AssetSectionRule testid={`asset-manager-section-${section.key}`}>{section.label}</AssetSectionRule>
                {section.groups.map((group) => (
                  <Fragment key={group.type}>
                    <AssetTypeHeader testid={`asset-manager-group-${section.key}-${group.type}`}>
                      {group.label}
                    </AssetTypeHeader>
                    {group.rows.map((row) => (
                      <AssetRow
                        key={row.key}
                        descriptor={row.d}
                        scope={row.scope}
                        label={row.label}
                        selected={row.selected}
                        improvable={row.improvable}
                        busy={improveBusyKey === row.key}
                        onPick={onPick ? handlePick : undefined}
                        onUnpick={onUnpick}
                        onImprove={onImprove}
                        openInWindow={openInWindow}
                      />
                    ))}
                  </Fragment>
                ))}
              </Fragment>
            ))}
            {unknownUsage.length > 0 && (
              <>
                <AssetSectionRule testid="asset-manager-section-unknown">
                  <Trans>Unknown assets ({unknownUsage.length})</Trans>
                </AssetSectionRule>
                {unknownUsage.map((usage) => (
                  <UnknownAssetRow key={`${usage.resolution}:${usage.reference}`} usage={usage} />
                ))}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b px-3 py-2">
            <input
              autoFocus
              type="text"
              placeholder={t`Search projects…`}
              value={projectQuery}
              onChange={(e) => setProjectQuery(e.target.value)}
              className="w-full rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
              data-testid="asset-manager-project-search"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filteredProjects.length === 0 && (
              <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">
                <Trans>No projects.</Trans>
              </div>
            )}
            {filteredProjects.map((p) => {
              const path = (p as { fs_storage_mount_path?: string }).fs_storage_mount_path ?? '';
              return (
                <ProjectPickRow key={p.id} name={p.displayName ?? p.id ?? ''} path={path} onPick={handlePickProject} />
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  // Two containers, one body. `centered` escapes an enclosing menu (the
  // bookmarks tree, the composer's attach fan-out) where an anchored popover
  // would be trapped or immediately dismissed.
  if (centered) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex max-h-[min(85vh,40rem)] w-[30rem] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0"
          data-testid="asset-manager-popover"
        >
          <DialogTitle className="sr-only">
            <Trans>Assets</Trans>
          </DialogTitle>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        side={side}
        sideOffset={4}
        collisionPadding={8}
        className="flex max-h-[min(calc(100vh-6rem),var(--radix-popover-content-available-height))] w-[30rem] max-w-[calc(100vw-2rem)] flex-col p-0"
        data-testid="asset-manager-popover"
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}

// ── Row components ────────────────────────────────────────────────────────────

/** A skill's folder, not its SKILL.md: ".../skills/rca/SKILL.md" → "rca". */
function unknownUsageName(reference: string): string {
  const path = normalizePath(reference);
  const last = _basename(path);
  return /^SKILL\.md$/i.test(last) && path.includes('/') ? _basename(dirname(path)) : last;
}

/** Every outcome that lands in the unknown list needs a reason — a new one breaks the build. */
type UnknownResolution = Exclude<ProcessAssetUsage['resolution'], 'resolved' | 'builtin'>;
const UNKNOWN_REASONS: Record<UnknownResolution, MessageDescriptor> = {
  missing: msg`Not found on disk`,
  unbound: msg`Unknown name`,
  ambiguous: msg`Several matches`,
  identity_changed: msg`Changed since use`,
};

function UnknownAssetRow({ usage }: { usage: ProcessAssetUsage }) {
  const { t } = useLingui();
  const reason = UNKNOWN_REASONS[usage.resolution as UnknownResolution];
  return (
    <AssetGridRow data-testid="asset-unresolved-usage" data-resolution={usage.resolution}>
      <span
        className="flex min-w-0 items-center gap-1.5 rounded border border-dashed px-1.5 py-0.5 text-xs text-muted-foreground"
        title={usage.reference}
      >
        <CircleHelp className="h-3 w-3 flex-shrink-0" aria-hidden />
        <span className="truncate">{unknownUsageName(usage.reference)}</span>
      </span>
      <span className="truncate text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="asset-unresolved-reason">
        {reason ? t(reason) : usage.resolution}
      </span>
      <GridCellSpacer />
      <GridCellSpacer />
      <GridCellSpacer />
    </AssetGridRow>
  );
}

function DirRow({ path, onRemove }: { path: string; onRemove?: (path: string) => void | Promise<void> }) {
  const { t } = useLingui();
  return (
    <AssetGridRow data-testid={`asset-manager-dir-row-${path}`}>
      <span className="flex min-w-0 items-center gap-1.5 text-xs text-foreground" title={path}>
        <Folder className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate">{_basename(path) || path}</span>
      </span>
      {/* Through the scope model like every other row: hand-rolling the chip here
          is how this dir ended up wearing the context-folder glyph while the
          assets inside it wore the additional-dir one. */}
      <AssetScopeChip scope={additionalDirScope(path)} testidSuffix={`dir-${path}`} />
      <GridCellSpacer />
      <GridCellSpacer />
      {onRemove ? (
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => void onRemove(path)}
          title={t`Remove directory`}
          data-testid={`asset-manager-dir-remove-${path}`}
        >
          <X className="h-3 w-3" />
        </button>
      ) : (
        <GridCellSpacer />
      )}
    </AssetGridRow>
  );
}

function ProjectPickRow({
  name,
  path,
  onPick,
}: {
  name: string;
  path: string;
  onPick: (path: string) => void | Promise<void>;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-2 border-b px-3 py-1.5 text-start last:border-b-0 hover:bg-muted/50"
      onClick={() => void onPick(path)}
      data-testid={`asset-manager-project-pick-${path}`}
    >
      <FolderPlus className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs text-foreground" title={name}>
          {name}
        </div>
        <div className="truncate text-[10px] text-muted-foreground" title={path}>
          {path}
        </div>
      </div>
    </button>
  );
}

export function AssetRow({
  descriptor,
  scope,
  label,
  selected,
  improvable,
  busy,
  canOpen,
  cannotOpenReason,
  openAction,
  onPick,
  onUnpick,
  onImprove,
  openInWindow = false,
}: {
  descriptor: AssetDescriptor;
  /** Resolved by the list memo — all of these are cache lookups or allocations,
   *  so they are derived once there rather than per render here. */
  scope: AssetScope;
  label: string;
  selected: boolean;
  improvable: boolean;
  busy: boolean;
  /** Host veto on the open affordance. Openability is otherwise derived from the
   *  typeid alone, which answers "is this a resolvable entity" — not "does an
   *  editor exist for it". A capability has a real `capability-<uuid>` and no
   *  editor, so the derived answer is yes and the click dead-ends. Defaults to
   *  the derived value; `false` disables the control. */
  canOpen?: boolean;
  /** Why the row cannot be opened, for the disabled tooltip. Without it the
   *  vetoed row claims to be an inline persona, which is a different thing. */
  cannotOpenReason?: string;
  /** Host-owned open action, REPLACING the derived "open in its asset editor"
   *  navigation — and making the row openable regardless of what the typeid
   *  says. For a row whose type has no editor (`data_source`, configured in the
   *  Data sources view rather than opened as a file) the derived route falls
   *  back to the markdown editor and dead-ends; the host knows where its row
   *  actually leads, so it says so. */
  openAction?: { label: string; run: (descriptor: AssetDescriptor) => void };
  onPick?: (descriptor: AssetDescriptor) => void | Promise<void>;
  onUnpick?: (descriptor: AssetDescriptor) => void | Promise<void>;
  onImprove?: (descriptor: AssetDescriptor) => void;
  /** See ``AssetManagerPopoverProps.openInWindow``. */
  openInWindow?: boolean;
}) {
  const { t } = useLingui();
  const { navigation } = useDockNavigation();
  const { type, id } = _parseTypeid(descriptor.typeid);
  const readOnly = isReadOnlySource(descriptor.source);
  const openable = (canOpen ?? true) && (!!openAction || _isOpenableTypeid(descriptor.typeid));
  const locationText = useEntityLocationLabel(descriptor.remote);
  // The name chip OPENS the asset in its editor — clicking the thing you are
  // looking at should show you the thing. Selecting is a separate, explicit
  // control at the row end, and only exists where a host asked for it: on the
  // process asset manager this list is a read-only "used vs available" board.
  const togglesOff = selected && !!onUnpick;
  const pickLabel = togglesOff ? t`Remove ${label}` : t`Select ${label}`;
  const openActionLabel = !openable
    ? (cannotOpenReason ?? t`Inline persona — no backing entity`)
    : openAction
      ? openAction.label
      : readOnly
        ? t`View ${label} (read-only)`
        : t`Open ${label}`;
  const openActionTitle = locationText ? `${openActionLabel}\n${locationText}` : openActionLabel;
  const openActionAria = locationText ? `${openActionLabel}, ${locationText}` : openActionLabel;
  // Explorer targets the FILE, so an asset with no path (inline persona) has
  // nowhere to go — same "no backing file" story as a non-openable row.
  const explorerPath = descriptor.posix_path ?? null;
  const explorerLabel = explorerPath
    ? t`Show ${label} in Files`
    : (cannotOpenReason ?? t`Inline persona — no file on disk`);

  const onOpenExplorerClick = useCallback(() => {
    if (!explorerPath) return;
    try {
      // Read at CLICK time, not through `useContext`: that hook's snapshot spans
      // ~30 fields that churn on a live process, and this list mounts one row
      // per asset — subscribing per row would re-render the whole board on
      // every terminal tick for a value only this handler ever reads.
      const computeNode = dataContext.computeNode?.typeId ?? LOCAL_COMPUTE_NODE;
      navigation.openDock(DockPointer.forExplorer(VFSPath.fromMachinePath(explorerPath, computeNode).absVfsPath));
    } catch (err) {
      console.error('[AssetRow] failed to open asset in explorer', explorerPath, err);
    }
  }, [explorerPath, navigation]);

  const onOpenClick = useCallback(() => {
    if (!openable) return;
    if (openAction) {
      openAction.run(descriptor);
      return;
    }
    if (!id) return;
    try {
      const options = { assetType: type, ...(readOnly ? { readOnly: '1' } : {}) };
      const computeNode = dataContext.computeNode?.typeId ?? LOCAL_COMPUTE_NODE;
      const pointer = descriptor.posix_path
        ? DockPointer.forAssetEditor(
            type, VFSPath.fromMachinePath(descriptor.posix_path, computeNode).absVfsPath,
            undefined, options,
          )
        : DockPointer.forAssetEditorByTypeId(type, new TypeId(type, id), undefined, options);
      if (openInWindow) navigation.openDockInWindow(pointer);
      else navigation.openDock(pointer);
    } catch (err) {
      console.error('[AssetRow] failed to open asset', descriptor.typeid, err);
    }
  }, [navigation, type, id, readOnly, openable, openAction, descriptor, openInWindow]);

  return (
    <AssetGridRow
      className={selected ? 'bg-primary/5' : undefined}
      data-testid={`asset-manager-row-${descriptor.typeid}-${descriptor.source}`}
      data-read-only={readOnly ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      data-scope={scope.kind}
    >
      <button
        type="button"
        onClick={onOpenClick}
        disabled={!openable}
        data-openable={openable ? 'true' : 'false'}
        className={cn(
          'flex min-w-0 items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs disabled:cursor-default disabled:opacity-60',
          selected
            ? 'border-primary/50 bg-primary/10 text-foreground hover:bg-primary/20'
            : 'border-border bg-muted/30 text-foreground hover:bg-muted',
        )}
        title={openActionTitle}
        aria-label={openActionAria}
        data-testid={`asset-manager-open-${descriptor.typeid}-${descriptor.source}`}
      >
        <EntityIcon
          type={type}
          remote={descriptor.remote}
          showLocationTooltip={false}
          density="compact"
          className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground"
        />
        <span className="min-w-0 truncate">{label}</span>
      </button>
      <AssetScopeChip scope={scope} testidSuffix={`${descriptor.typeid}-${descriptor.source}`} />
      <button
        type="button"
        onClick={onOpenExplorerClick}
        disabled={!explorerPath}
        data-openable={explorerPath ? 'true' : 'false'}
        className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-40"
        title={explorerLabel}
        aria-label={explorerLabel}
        data-testid={`asset-manager-explorer-${descriptor.typeid}-${descriptor.source}`}
      >
        <SquareArrowOutUpRight className="h-3 w-3" />
      </button>
      {improvable && onImprove ? (
        <WikiTip
          wikiword={ASSET_IMPROVEMENT_WIKI}
          label={t`Analyze and improve`}
          buttonLabel={t`How does improving an asset work?`}
        >
          <button
            type="button"
            className="flex h-5 w-5 items-center justify-center rounded text-primary hover:bg-primary/10 disabled:opacity-60"
            onClick={() => onImprove(descriptor)}
            disabled={busy}
            aria-label={t`Analyze and improve`}
            data-testid={`asset-manager-improve-${descriptor.typeid}-${descriptor.source}`}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <WandSparkles className="h-3 w-3" />}
          </button>
        </WikiTip>
      ) : (
        <GridCellSpacer />
      )}
      {/* The SELECT control. No read-only guard here: read-only describes whether
          the asset FILE can be edited, not whether the user's own selection can
          be undone. Most staged rows are read-only sources, so gating on it hid
          the control exactly where it is needed most.

          Absent entirely when the host supplied no selection callbacks — that is
          what turns this list into a read-only availability board. */}
      {togglesOff || onPick ? (
        <button
          type="button"
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded hover:bg-muted hover:text-foreground',
            togglesOff ? 'text-primary' : 'text-muted-foreground',
          )}
          onClick={() => {
            void (togglesOff ? onUnpick(descriptor) : onPick?.(descriptor));
          }}
          title={pickLabel}
          aria-label={pickLabel}
          aria-pressed={selected}
          data-testid={
            togglesOff
              ? `asset-manager-unselect-${descriptor.typeid}-${descriptor.source}`
              : `asset-manager-select-${descriptor.typeid}-${descriptor.source}`
          }
        >
          {togglesOff ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
        </button>
      ) : (
        <GridCellSpacer />
      )}
    </AssetGridRow>
  );
}
